import { useState } from "react";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { Coins, Eye, Globe } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Admin-only hover card that reveals a user's submitted websites, the credits
 * assigned to each site, and the views (hits) each site has received. Data is
 * fetched lazily the first time the card opens for a given user, then cached
 * on the component instance so subsequent hovers are instant.
 */
export default function AdminUserSitesHoverCard({ user, children }) {
  const [sites, setSites] = useState(null); // null = not loaded, [] = loaded empty
  const [loading, setLoading] = useState(false);

  const onOpenChange = async (open) => {
    if (!open || sites !== null || loading || !user?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${user.id}/websites`);
      setSites(data.websites || []);
    } catch {
      setSites([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <HoverCard openDelay={120} closeDelay={80} onOpenChange={onOpenChange}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        align="start"
        sideOffset={8}
        className="w-80 p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl rounded-2xl"
        data-testid={`admin-user-sites-hover-${user?.username || user?.id}`}
      >
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100 dark:border-slate-800">
          <Globe size={16} weight="bold" className="text-blue-600" />
          <div className="text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Sites submitted by
          </div>
          <div className="text-sm font-bold text-slate-900 dark:text-white ml-auto truncate">
            @{user?.username}
          </div>
        </div>

        {loading && (
          <div className="text-xs text-slate-500 text-center py-4">
            Loading&hellip;
          </div>
        )}

        {!loading && sites !== null && sites.length === 0 && (
          <div className="text-xs text-slate-500 text-center py-4 rounded-lg border border-dashed border-slate-200 dark:border-slate-700">
            No websites submitted yet.
          </div>
        )}

        {!loading && sites && sites.length > 0 && (
          <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {sites.map((s) => (
              <li
                key={s.id}
                data-testid={`admin-hover-site-${s.id}`}
                className="rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 p-2.5"
              >
                <div
                  className="text-xs font-bold text-slate-900 dark:text-white truncate leading-tight"
                  title={s.title || s.url}
                >
                  {s.title || s.url}
                </div>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] text-blue-700 dark:text-blue-400 hover:underline break-all block leading-tight"
                  onClick={(e) => e.stopPropagation()}
                >
                  {s.url}
                </a>
                <div className="mt-1.5 flex items-center gap-3 text-[11px]">
                  <span
                    className="inline-flex items-center gap-1 text-slate-700 dark:text-slate-200 font-bold"
                    title="Credits assigned to this site"
                    data-testid={`admin-hover-site-credits-${s.id}`}
                  >
                    <Coins size={11} weight="fill" className="text-yellow-500" />
                    {Number(s.credits_assigned || 0).toFixed(0)} credits
                  </span>
                  <span
                    className="inline-flex items-center gap-1 text-slate-700 dark:text-slate-200 font-bold"
                    title="Total visits (hits) this site has received"
                    data-testid={`admin-hover-site-hits-${s.id}`}
                  >
                    <Eye size={11} weight="fill" className="text-blue-500" />
                    {Number(s.views_earned || 0).toFixed(0)} hits
                  </span>
                  <span
                    className={`ml-auto text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full ${
                      s.status === "approved"
                        ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"
                        : s.status === "pending"
                        ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300"
                        : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                    }`}
                  >
                    {s.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
